import { createElement } from 'react';
import { Shield, Unlock, Users, Lightbulb, Globe, Heart } from 'lucide-react';

const Values = () => {
  const values = [
    {
      icon: Unlock,
      iconClass: "text-primary-600 dark:text-white",
      title: "Open Source",
      description: "Encourage knowledge and resource sharing to stimulate collaborative and accessible innovation."
    },
    {
      icon: Users,
      iconClass: "text-primary-600 dark:text-white",
      title: "Inclusion",
      description: "Create an open space promoting diversity, participation, and equal opportunity."
    },
    {
      icon: Lightbulb,
      iconClass: "text-primary-600 dark:text-white",
      title: "Apprentissage Pratique",
      description: "Place experimentation and hands-on experience at the center of sustainable skill development."
    },
    {
      icon: Shield,
      iconClass: "text-primary-600 dark:text-white",
      title: "Autonomie",
      description: "Develop technical independence and confidence to innovate and build."
    },
    {
      icon: Globe,
      iconClass: "text-primary-600 dark:text-white",
      title: "Impact Local",
      description: "Strengthen and support the regional innovation ecosystem."
    },
    {
      icon: Heart,
      iconClass: "text-primary-600 dark:text-white",
      title: "Passion & Excellence",
      description: "Combine rigor, quality, and commitment in every project and training program."
    }
  ];

  const achievements = [
    {
      number: "10+",
      label: "Machines Professionnelles",
      description: "Équipements avancés pour tous types de projets"
    },
    {
      number: "150m²",
      label: "Espace de Travail",
      description: "Ateliers modulables et zones collaboratives"
    },
    {
      number: "365 Jours",
      label: "de Support",
      description: "Assistance technique et pédagogique continue"
    }
  ];

  return (
    <section id="values" className="py-20 bg-gray-50 dark:bg-gray-900 relative">
      <div className="absolute inset-0 bg-[linear-gradient(rgba(0,0,0,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(0,0,0,0.02)_1px,transparent_1px)] dark:bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:20px_20px]"></div>
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6">Nos Valeurs</h2>
          <p className="text-xl text-gray-700 dark:text-white/70">Les principes qui guident notre action au quotidien</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {values.map((value, index) => (
            <div 
              key={index}
              className="group bg-white/5 backdrop-blur-sm rounded-xl p-8 border border-white/10 hover:bg-white/10 hover:border-white/20 transition-all duration-500 transform hover:-translate-y-2"
            >
              <div className="flex flex-col items-center text-center">
                <div key={index} className="bg-white/90 dark:bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-gray-200 dark:border-white/10 hover:border-gray-300 dark:hover:border-white/20 transition-all duration-300 h-full shadow-sm hover:shadow-md">
                  <div className="w-12 h-12 bg-primary-100 dark:bg-white/10 rounded-lg flex items-center justify-center mb-4">
                    {createElement(value.icon, { className: `${value.iconClass} h-6 w-6` })}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">{value.title}</h3>
                  <p className="text-gray-600 dark:text-white/70">{value.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 md:p-12 border border-white/10 mb-16">
          <div className="mt-20 bg-gradient-to-r from-primary-100 to-purple-100 dark:from-primary-500/10 dark:to-purple-500/10 backdrop-blur-sm rounded-3xl p-8 md:p-12 border border-gray-200 dark:border-white/10 shadow-sm">
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Fab43 en Chiffres</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {achievements.map((achievement, index) => (
                <div key={index} className="text-center">
                  <div className="text-4xl font-bold text-gray-900 dark:text-white mb-2">{achievement.number}</div>
                  <div className="text-primary-600 dark:text-primary-300 font-medium">{achievement.label}</div>
                  <div className="text-gray-600 dark:text-white/60 text-sm mt-1">{achievement.description}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="bg-white/90 dark:bg-white/5 backdrop-blur-sm rounded-2xl p-8 md:p-12 text-center border border-gray-200 dark:border-white/10 shadow-sm">
          <h3 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">Rejoignez l'Aventure Fab43</h3>
          <p className="text-xl text-gray-600 dark:text-white/70 mb-8 max-w-3xl mx-auto">
            Que vous soyez débutant curieux ou expert confirmé, Fab43 vous offre l'environnement 
            idéal pour concrétiser vos projets les plus ambitieux.
          </p>
          <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4">
            <a 
              href="https://wa.me/21624973271?text=Bonjour%2C%20je%20souhaite%20devenir%20membre%20de%20Fab43"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-primary-600 hover:bg-primary-700 text-white px-8 py-4 rounded-full font-semibold transition-all duration-300 transform hover:scale-105 inline-block text-center"
            >
              Devenir Membre
            </a>
            <a 
              href="https://wa.me/21624973271?text=Bonjour%2C%20je%20souhaite%20programmer%20une%20visite%20de%20Fab43"
              target="_blank"
              rel="noopener noreferrer"
              className="border-2 border-primary-600 text-primary-600 dark:border-primary-500 dark:text-primary-400 px-8 py-4 rounded-full font-semibold hover:bg-primary-50 dark:hover:bg-primary-500/10 transition-all duration-300 inline-block text-center"
            >
              Programmer une Visite
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Values;